import { loadScript } from '../utils/loadScript';
import { acquireAdSlot } from './adSlot';
import { adsRuntimeReady } from './adsRuntime';

const AD_SCRIPT_TIMEOUT = 5000;
const AD_RETRY_DELAY_MS = 10000;
// Пауза после тика шедулера, чтобы стартовая цепочка Adsgram не пошла впритык.
const AD_SLOT_COOLDOWN_MS = 10000;

export interface AdInitResult {
  taddy: boolean;
  adexium: boolean;
  monetag: boolean;
  adsgram: boolean;
  telegramads: boolean;
}

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

async function loadScriptWithRetry(
  label: string,
  url: string,
  options: Parameters<typeof loadScript>[1],
  onSuccess: () => void,
  result: AdInitResult,
  key: keyof AdInitResult,
): Promise<void> {
  // Бесконечные попытки загрузить SDK, пока не получится
  // Между попытками выдерживаем паузу AD_RETRY_DELAY_MS
  // Таймаут отдельной попытки задаётся через AD_SCRIPT_TIMEOUT
  // (options.timeout)
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const res = await loadScript(url, options);
    if (res.success) {
      result[key] = true;
      try {
        onSuccess();
      } catch (e) {
        console.error(`[Ads] ${label} init error:`, (e as Error).message);
      }
      console.log(`[Ads] ${label} SDK loaded`);
      return;
    }

    console.warn(`[Ads] ${label} SDK failed to load, retrying in ${AD_RETRY_DELAY_MS}ms:`, res.error?.message);
    await delay(AD_RETRY_DELAY_MS);
  }
}


export async function initAds(): Promise<AdInitResult> {
  // Ждём рантайм-скрипт совместимости: он должен встать раньше рекламных SDK.
  await adsRuntimeReady();

  const result: AdInitResult = {
    taddy: false,
    adexium: false,
    monetag: false,
    adsgram: false,
    telegramads: false,
  };

  await Promise.all([
    loadScriptWithRetry(
      'Taddy',
      'https://sdk.taddy.pro/web/taddy.min.js',
      {
        timeout: AD_SCRIPT_TIMEOUT,
        attrs: { 'data-pub-id': 'd6ee8535c767c0fcd98fec3da11815c5' },
      },
      () => initTaddy(),
      result,
      'taddy',
    ),

    loadScriptWithRetry(
      'Adexium',
      'https://cdn.adexium.tech/assets/js/adexium-widget.min.js',
      {
        timeout: AD_SCRIPT_TIMEOUT,
      },
      () => initAdexium(),
      result,
      'adexium',
    ),

    loadScriptWithRetry(
      'Monetag',
      'https://munqu.com/sdk.js',
      {
        timeout: AD_SCRIPT_TIMEOUT,
        attrs: { 'data-zone': '10280884', 'data-sdk': 'show_10280884' },
      },
      () => {
        // Для Monetag отдельной инициализации нет
      },
      result,
      'monetag',
    ),

    loadScriptWithRetry(
      'TelegramAds',
      'https://richinfo.co/richpartners/telegram/js/tg-ob.js',
      {
        timeout: AD_SCRIPT_TIMEOUT,
      },
      () => initTelegramAds(),
      result,
      'telegramads',
    ),

  ]);

  console.log('[Ads] All SDK load attempts completed (at least once per network):', result);
  return result;
}

function initTaddy(): void {
  try {
    const Taddy = (window as any).Taddy;
    if (Taddy && typeof Taddy.ready === 'function') {
      Taddy.ready();
      console.log('[Taddy] ready() sent');
    }
  } catch (e) {
    console.error('[Taddy] ready error', e);
  }
}

function initAdexium(): void {
  try {
    const AdexiumWidget = (window as any).AdexiumWidget;
    if (!AdexiumWidget) return;

    const adexiumPush = new AdexiumWidget({
      wid: 'e8744c38-2452-4b79-8e11-ae5165d14ebc',
      adFormat: 'push-like',
      debug: false,
      firstAdImpressionIntervalInSeconds: 15,
      adImpressionIntervalInSeconds: 90,
      isFullScreen: false,
    });
    adexiumPush.autoMode();

    const adexiumInterstitial = new AdexiumWidget({
      wid: 'c8e3b41e-400e-40b1-bf10-5a06b2890f41',
      adFormat: 'interstitial',
      debug: false,
      isFullScreen: true,
    });
    (window as any).adexiumInterstitialInstance = adexiumInterstitial;

    console.log('[Adexium] widgets initialized');
  } catch (e) {
    console.error('[Adexium] init error', e);
  }
}

function initTelegramAds(): void {
  try {
    const TelegramAdsController = (window as any).TelegramAdsController;

    console.log('[TelegramAds] Checking SDK availability...');
    console.log('[TelegramAds] TelegramAdsController class:', TelegramAdsController);
    console.log('[TelegramAds] Type:', typeof TelegramAdsController);

    if (!TelegramAdsController) {
      console.error('[TelegramAds] SDK class not found in window');
      return;
    }

    // Создаем экземпляр контроллера
    console.log('[TelegramAds] Creating controller instance...');
    const controller = new TelegramAdsController();
    console.log('[TelegramAds] Controller instance created:', controller);

    if (!controller) {
      console.error('[TelegramAds] Controller instance is null');
      return;
    }

    console.log('[TelegramAds] Controller methods:', Object.keys(controller));
    console.log('[TelegramAds] triggerNativeNotification exists:', typeof controller.triggerNativeNotification);
    console.log('[TelegramAds] triggerInterstitialBanner exists:', typeof controller.triggerInterstitialBanner);

    console.log('[TelegramAds] Calling initialize with pubId: 979651, appId: 6561');
    controller.initialize({
      pubId: "979651",
      appId: "6561",
      debug: false
    });

    console.log('[TelegramAds] Initialize called, debug mode enabled');

    // Сохраняем экземпляр для использования
    (window as any).telegramAdsInstance = controller;

    console.log('[TelegramAds] Initialization complete, instance saved to window.telegramAdsInstance');
    console.log('[TelegramAds] triggerNativeNotification available:', typeof controller.triggerNativeNotification);
  } catch (e) {
    console.error('[TelegramAds] init error', e);
  }
}


export function startAdsScheduler(): void {
  const MIN_INTERVAL_MS = 2 * 60 * 1000;

  let lastFullscreenAdTs = 0;
  let fullscreenAdToggle = 0; // для чередования: Adexium сначала или Monetag сначала

  setInterval(async () => {
    const now = Date.now();
    if (now - lastFullscreenAdTs < MIN_INTERVAL_MS) return;

    // Экран может быть занят цепочкой Adsgram (стартовой или от кнопки пользователя) —
    // тогда пропускаем тик целиком и пробуем на следующем.
    const releaseSlot = acquireAdSlot('scheduler');
    if (!releaseSlot) return;

    // Сразу обновляем таймер чтобы избежать спама
    lastFullscreenAdTs = now;

    const useAdexiumFirst = fullscreenAdToggle % 2 === 0;
    fullscreenAdToggle++;

    const tryShowAdexium = async (): Promise<boolean> => {
      const instance = (window as any).adexiumInterstitialInstance;
      if (!instance?.show) return false;
      try {
        await instance.show();
        lastFullscreenAdTs = Date.now();
        return true;
      } catch {
        return false;
      }
    };

    // Adsgram interstitial запускается только из startupAds/переходов. Не
    // запускаем его здесь параллельно с Monetag и Telegram Ads: одновременно
    // может быть только один fullscreen-формат.
    const tryShowMonetag = async (): Promise<boolean> => {
      const showMonetag = (window as any).show_10280884;
      if (typeof showMonetag !== 'function') return false;
      try {
        const ymid = localStorage.getItem('telegram_id') || undefined;

        // Telegram Ads controller
        const telegramController = (window as any).telegramAdsInstance;
        const canUseTelegramAds = telegramController && typeof telegramController.triggerNativeNotification === 'function';

        const monetagPromise = (async () => {
          await showMonetag({ type: 'preload', ymid });
          await showMonetag({ ymid });
        })();

        // Telegram Ads: Native + Interstitial с задержкой 1 сек между ними
        const telegramNativePromise = canUseTelegramAds
          ? (async () => {
              try {
                await telegramController.triggerNativeNotification(false);
              } catch (e) {
                console.error('[TelegramAds Scheduler] triggerNativeNotification error:', e);
              }
            })()
          : Promise.resolve();

        const telegramInterstitialPromise = canUseTelegramAds && typeof telegramController.triggerInterstitialBanner === 'function'
          ? (async () => {
              await delay(1000); // задержка 1 сек после Native
              try {
                await telegramController.triggerInterstitialBanner(false);
              } catch (e) {
                console.error('[TelegramAds Scheduler] triggerInterstitialBanner error:', e);
              }
            })()
          : Promise.resolve();

        // Ждём завершения остальных независимых провайдеров. Adsgram сюда не
        // входит — его fullscreen-показ координируется отдельно.
        await Promise.allSettled([
          monetagPromise,
          telegramNativePromise,
          telegramInterstitialPromise
        ]);

        // Обновляем таймер после любого показа
        lastFullscreenAdTs = Date.now();
        return true;
      } catch {
        return false;
      }
    };

    // Чередуем: один раз сначала Adexium, потом Monetag;
    // в следующий тик — сначала Monetag, потом Adexium.
    try {
      if (useAdexiumFirst) {
        if (!(await tryShowAdexium())) await tryShowMonetag();
      } else {
        if (!(await tryShowMonetag())) await tryShowAdexium();
      }
    } finally {
      releaseSlot(AD_SLOT_COOLDOWN_MS);
    }
  }, 10000);
}
